/**
 * Permission maps — single source of truth for all permission keys.
 * Keys mirror the API JSON structure in `access_config`.
 *
 * Three scopes: global, stage, source.
 * Four control values: "allow" | "deny" | "view" | "own"
 *
 * Niche global modules (industry-specific) are unioned from `@/rbac/industry/registry`.
 */

import { buildAllNicheGlobalPermissionMap } from "@/rbac/industry";

const A = "allow" as const;

/* ── Global: lead_management ── */

export const LEAD_MANAGEMENT_MAP = {
  create: A,
  view: A,
  edit: A,
  delete: A,
  bulk_edit: A,
  view_activity: A,
  /** Hidden by default; gates visibility of rows that were marked archived
   *  during a previous reassignment / branch-transfer. */
  view_archived_history: A,
} as const;

/* ── Global: assignation ── */

export const ASSIGNATION_MANAGEMENT_MAP = {
  lead_assignation: A,
  child_assignation: A,
  branch_assignation: A,
} as const;

/* ── Global: core modules ── */

export const GLOBAL_CORE_PERMISSION_MAP: Record<
  string,
  Record<string, string>
> = {
  lead_management: LEAD_MANAGEMENT_MAP,
  assignation_management: ASSIGNATION_MANAGEMENT_MAP,
  /* dashboard_management: dynamic — widget keys come from the tenant's dashboard
   * config (industry-dependent). Built at runtime in usePermissionBlueprint and
   * the role form; not listed here because the action keys vary per tenant. */
  user_management: { create: A, edit: A, view: A, delete: A },
  branch_management: { create: A, edit: A, view: A, delete: A },
  role_management: { create: A, edit: A, view: A, delete: A },
  custom_management: { create: A, edit: A, view: A, delete: A },
  /* Razorpay payment-gateway permissions. Server-side blueprint lives at
   * src/rbac/permissions/razorpay.permissions.ts. */
  razorpay: {
    manage_integration: A,
    create_payment_link: A,
    view_payment_links: A,
    cancel_payment_link: A,
    /* The Razorpay page (sidebar INTEGRATION group): read every resource /
     * money moves (capture, refund, settle, transfer, disputes) / records
     * (orders, links, invoices, items, plans, subscriptions, customers, …). */
    view_console: A,
    manage_payments: A,
    manage_billing: A,
  },
  /* Dynamic keys `custom_field_{customInputId}` (not listed here): value access on records
   * — `{ view: ControlType, edit: ControlType }` (legacy `{ access }` is normalized away). */
};

/* ── Global: combined (core + union of all niche module keys from industry registry) ── */

export const GLOBAL_PERMISSION_MAP: Record<string, Record<string, string>> = {
  ...GLOBAL_CORE_PERMISSION_MAP,
  ...buildAllNicheGlobalPermissionMap(),
};

/* ── Stage permissions ── */

export const STAGE_PERMISSION_MAP: Record<string, Record<string, string>> = {
  stage_management: {
    create: A,
    manage: A,
    update: A,
    move: A,
    rollback: A,
    delete: A,
    /** Stage-scoped filter / saved-view UI (see role editor Stage section). */
    filter_view: A,
    filter_edit: A,
  },
  choice_management: {
    create: A,
    edit: A,
    view: A,
    delete: A,
  },
  document_management: {
    create: A,
    manage: A,
    view: A,
    edit: A,
    update: A,
    delete: A,
    download: A,
  },
  track_management: {
    create: A,
    view: A,
    edit: A,
    delete: A,
    manage: A,
  },
};

/** Every stage-scoped action set to `"deny"` (e.g. new pipeline stages merged into an existing role). */
export function buildDenyStagePermissionBlueprint(): Record<
  string,
  Record<string, string>
> {
  const o: Record<string, Record<string, string>> = {};
  for (const [mod, actions] of Object.entries(STAGE_PERMISSION_MAP)) {
    o[mod] = Object.fromEntries(
      Object.keys(actions).map((k) => [k, "deny" as const])
    );
  }
  return o;
}

/* ── Source permissions ── */

export const SOURCE_PERMISSION_MAP: Record<string, Record<string, string>> = {
  source_access: {
    view: A,
    create: A,
    assign: A,
  },
};

/* ── Simple type aliases (no generics) ── */

export type GlobalModuleKey = string;
export type GlobalAction = string;
export type StageModuleKey = string;
export type StageAction = string;
export type StageActionKey = string;
export type SourceModuleKey = string;
export type SourceAction = string;
